<template>
  <section class="invoice-preview-wrapper">
    <!-- Alert: No item found -->
    <b-alert variant="danger" :show="orderData === undefined">
      <h4 class="alert-heading">Error fetching invoice data</h4>
      <div class="alert-body">
        No invoice found with this invoice id. Check
        <b-link class="alert-link" :to="{ name: 'apps-invoice-list' }">
          invoice List
        </b-link>
        for other invoices.
      </div>
    </b-alert>

    <b-row v-if="orderData" class="invoice-preview">
      <!-- Col: Left (Invoice Container) -->
      <b-col cols="12" xl="9" md="8">
        <b-card no-body class="invoice-preview-card size-print-page">
          <!-- Header -->
          <b-card-body class="invoice-padding pb-0">
            <div
              class="
                d-flex
                justify-content-between
                flex-md-row flex-column
                invoice-spacing
                mt-0
              "
            >
              <!-- Header: Left Content -->
              <div v-if="orderData.createdBy">
                <div class="logo-wrapper">
                  <logo />
                  <h3 class="text-primary invoice-logo">ERP NewTech</h3>
                </div>

                <b-card-text class="mb-25">
                  {{ orderData.createdBy.country }},
                  {{ orderData.createdBy.postalCode }}
                </b-card-text>
                <b-card-text class="mb-25">
                  {{ orderData.createdBy.address }}
                </b-card-text>
                <b-card-text class="mb-0">
                  Tel: {{ orderData.createdBy.companyTel }}, Fax:
                  {{ orderData.createdBy.companyFax }}
                </b-card-text>
                <b-card-text class="mb-0">
                  Email: {{ orderData.createdBy.companyEmail }}
                </b-card-text>
              </div>

              <!-- Header: Right Content -->
              <div class="mt-md-0 mt-2">
                <h4 class="invoice-title">
                  Bon de commande
                  <span class="invoice-number">#{{ orderData.code }}</span>
                </h4>
                <div class="invoice-date-wrapper">
                  <p class="invoice-date-title">Date de cré:</p>
                  <p class="invoice-date">
                    {{ externalDateFormat(orderData.createdAt) }}
                  </p>
                </div>
              </div>
            </div>
          </b-card-body>

          <!-- Spacer -->
          <hr class="invoice-spacing" />

          <!-- Invoice supplier & Payment Details -->
          <b-card-body v-if="orderData.supplier" class="invoice-padding pt-0">
            <b-row class="invoice-spacing">
              <!-- Col: Invoice To -->
              <b-col cols="12" xl="6" class="p-0">
                <h6 class="mb-2">Facture à:</h6>
                <h6 class="mb-25">
                  {{ orderData.supplier.firstName }}
                  {{ orderData.supplier.familyName }}
                </h6>
                <!-- <b-card-text class="mb-25">
                      {{ supplier.company }}
                    </b-card-text> -->
                <b-card-text class="mb-25">
                  {{ orderData.supplier.address }},
                  {{ orderData.supplier.city }}
                </b-card-text>
                <b-card-text class="mb-25">
                  {{ orderData.supplier.email }}
                </b-card-text>
                <b-card-text class="mb-0">
                  {{ orderData.supplier.telNumbers.toString() }}
                </b-card-text>
              </b-col>

              <!-- Col: Payment Details -->
              <b-col
                xl="6"
                cols="12"
                class="p-0 mt-xl-0 mt-2 d-flex justify-content-xl-end"
              >
                <div>
                  <h6 class="mb-2">Détails paiement:</h6>
                  <table>
                    <tbody v-if="orderData.createdBy">
                      <tr>
                        <td class="pr-1">Nom du bank :</td>
                        <td>{{ orderData.createdBy.bankName }}</td>
                      </tr>
                      <tr>
                        <td class="pr-1">IBAN:</td>
                        <td>{{ orderData.createdBy.iban }}</td>
                      </tr>
                      <tr>
                        <td class="pr-1">code SWIFT:</td>
                        <td>{{ orderData.createdBy.swiftCode }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </b-col>
            </b-row>
          </b-card-body>

          <!-- Invoice Description: Table -->
          <b-table-lite
            responsive
            :items="orderData.products"
            :fields="[
              { key: 'index', label: '#' },
              { key: 'product', label: 'Désignation' },
              { key: 'productPrice', label: 'Prix' },
              { key: 'quantity', label: 'Qté/Heu' },
              { key: 'valueAddedTax', label: 'Taxe' },
              { key: 'TotalePriceDutyFree', label: 'Totale HT' },
            ]"
          >
            <template #cell(index)="dataIndex">
              {{ dataIndex.index + 1 }}
            </template>
            <template #cell(product)="dataProduct">
              <b-card-text class="font-weight-bold mb-25">
                {{ dataProduct.item.product.label }}
              </b-card-text>
              <b-card-text class="text-nowrap product-description">
                {{ dataProduct.item.description }}
              </b-card-text>
            </template>
            <template #cell(productPrice)="dataPrice">
              {{ dataPrice.item.product.priceWithoutTaxes }}
            </template>
            <template #cell(quantity)="dataQuantity">
              {{ dataQuantity.item.quantity }}
              {{ dataQuantity.item.quantityType }}
            </template>
            <template #cell(TotalePriceDutyFree)="dataPrice">
              {{
                dataPrice.item.product.priceWithoutTaxes *
                dataPrice.item.quantity
              }}
            </template>
          </b-table-lite>
          <!-- Invoice Description: Total -->
          <b-card-body class="invoice-padding pb-0 price-footer-print">
            <hr class="invoice-spacing" />
            <b-row>
              <!-- Col: Sales Persion -->
              <b-col
                cols="12"
                md="6"
                class="mt-md-0 mt-3 remove-element-print"
                order="2"
                order-md="1"
              >
                <b-card-text class="mb-0">
                  <span class="font-weight-bold">Vendeur:</span>
                  <span class="ml-75">{{ orderData.salesperson }}</span>
                </b-card-text>
              </b-col>
              <b-col
                cols="12"
                md="6"
                class="mt-md-0 mt-3 add-element-print"
                order="2"
                order-md="1"
              >
                <b-card-text class="mb-0" v-if="printWithSignature">
                  <span class="font-weight-bold">Signature:</span>
                  <span class="mlt-50">
                    <img
                      :src="orderData.createdBy.signature"
                      alt="signature"
                      class="print-signature"
                  /></span>
                </b-card-text>
              </b-col>

              <!-- Col: Total -->
              <b-col
                cols="12"
                md="6"
                class="mt-md-6 d-flex justify-content-end"
                order="1"
                order-md="2"
              >
                <div class="invoice-total-wrapper">
                  <div class="invoice-total-item">
                    <p class="invoice-total-title">Timbre:</p>
                    <p class="invoice-total-amount">
                      {{ orderData.stampPrice }} {{ externalUserCurrency() }}
                    </p>
                  </div>
                  <div class="invoice-total-item">
                    <p class="invoice-total-title">Totale HT:</p>
                    <p class="invoice-total-amount">
                      {{ orderData.priceWithoutTaxes }}
                      {{ externalUserCurrency() }}
                    </p>
                  </div>
                  <div class="invoice-total-item">
                    <p class="invoice-total-title">Tax:</p>
                    <p class="invoice-total-amount">
                      {{ orderData.valueAddedTax }}
                      {{ externalUserCurrency() }}
                    </p>
                  </div>
                  <div class="invoice-total-item">
                    <p class="invoice-total-title">Totale av tax:</p>
                    <p class="invoice-total-amount">
                      {{
                        orderData.valueAddedTax + orderData.priceWithoutTaxes
                      }}
                      {{ externalUserCurrency() }}
                    </p>
                  </div>
                  <div class="invoice-total-item">
                    <p class="invoice-total-title">Remise:</p>
                    <p class="invoice-total-amount">
                      {{ orderData.totalDiscount }}
                      {{ externalUserCurrency() }}
                    </p>
                  </div>
                  <hr class="my-50" />
                  <div class="invoice-total-item">
                    <p class="invoice-total-title">Totale TTC:</p>
                    <p class="invoice-total-amount">
                      {{ orderData.allTaxesIncluded }}
                      {{ externalUserCurrency() }}
                    </p>
                  </div>
                </div>
              </b-col>
            </b-row>
          </b-card-body>

          <!-- Spacer -->
          <hr class="invoice-spacing" />

          <!-- Note -->
          <b-card-body
            class="invoice-padding pt-0 remove-element-print"
            v-if="orderData.note"
          >
            <span class="font-weight-bold">Note: </span>
            <span>{{ orderData.note }}</span>
          </b-card-body>
          <b-card-body
            class="invoice-padding pt-0 remove-element-print"
            v-if="orderData.paymentTerms"
          >
            <span class="font-weight-bold">Terme : </span>
            <span>{{ orderData.paymentTerms }}</span>
          </b-card-body>
        </b-card>
      </b-col>

      <!-- Right Col: Card -->
      <b-col cols="12" md="4" xl="3" class="invoice-actions">
        <b-card v-if="orderData.createdBy">
          <!-- Button: Send Invoice -->
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            v-b-toggle.sidebar-send-email
            variant="primary"
            class="mb-75"
            block
          >
            Envoyé Facture
          </b-button>

          <!-- Button: DOwnload -->
          <!-- <b-button
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="outline-secondary"
            class="mb-75"
            block
          >
            Télécharger
          </b-button> -->

          <!-- Button: Print -->
          <b-button
            v-if="orderData.createdBy.signature"
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="outline-secondary"
            class="mb-75"
            block
            @click="printInvoice(false)"
          >
            Imprimer avec signature
          </b-button>
          <b-button
            v-if="orderData.createdBy.signature"
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="outline-secondary"
            class="mb-75"
            block
            @click="printInvoice(true)"
          >
            Imprimer sans signature
          </b-button>
          <b-button
            v-if="!orderData.createdBy.signature"
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="outline-secondary"
            class="mb-75"
            block
            @click="printInvoice(true)"
          >
            Imprimer signature
          </b-button>

          <!-- Button: Edit -->
          <b-button
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="outline-secondary"
            class="mb-75"
            block
            @click="saveOrderPurchase()"
          >
            Enregistrer
          </b-button>

          <!-- Button: Add Payment -->
          <!-- <b-button
            v-b-toggle.sidebar-invoice-add-payment
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="success"
            class="mb-75"
            block
          >
            Add Payment
          </b-button> -->
        </b-card>

        <b-card v-else-if="orderData.createdBy">
          <!-- Button: Send Invoice -->
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            v-b-toggle.sidebar-send-email
            variant="primary"
            class="mb-75"
            block
          >
            Envoyé Facture
          </b-button>

          <!-- Button: DOwnload -->
          <!-- <b-button
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="outline-secondary"
            class="mb-75"
            block
          >
            Télécharger
          </b-button> -->

          <!-- Button: Print -->
          <b-button
            v-if="orderData.createdBy.signature"
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="outline-secondary"
            class="mb-75"
            block
            @click="printInvoice(false)"
          >
            Imprimer avec signature
          </b-button>
          <b-button
            v-if="orderData.createdBy.signature"
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="outline-secondary"
            class="mb-75"
            block
            @click="printInvoice(true)"
          >
            Imprimer sans signature
          </b-button>
          <b-button
            v-if="!orderData.createdBy.signature"
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="outline-secondary"
            class="mb-75"
            block
            @click="printInvoice(true)"
          >
            Imprimer signature
          </b-button>

          <!-- Button: Edit -->
          <b-button
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="outline-secondary"
            class="mb-75"
            block
            @click="saveOrderPurchase()"
          >
            Enregistrer
          </b-button>

          <!-- Button: Add Payment -->
          <!-- <b-button
            v-b-toggle.sidebar-invoice-add-payment
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="success"
            class="mb-75"
            block
          >
            Add Payment
          </b-button> -->
        </b-card>
      </b-col>
    </b-row>

    <sidebar-send-email
      :data="{
        name: 'Bon de commande',
        email: orderData.supplier.email,
        code: orderData.code,
      }"
    />
  </section>
</template>

<script>
// utils
import { DateFormat } from "@/utils/filter.js";
import { GetLinkCurrency } from "@/utils/utils.js";
import store from "@/store";
import router from "@/router";
import {
  BRow,
  BCol,
  BCard,
  BCardBody,
  BTableLite,
  BCardText,
  BButton,
  BAlert,
  BLink,
  VBToggle,
} from "bootstrap-vue";
import Logo from "@core/layouts/components/Logo.vue";
import Ripple from "vue-ripple-directive";
import SidebarSendEmail from "../../composants/sidebar/SidebarSendEmail.vue";
import axios from "axios";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

export default {
  directives: {
    Ripple,
    "b-toggle": VBToggle,
  },
  components: {
    ToastificationContent,
    BRow,
    BCol,
    BCard,
    BCardBody,
    BTableLite,
    BCardText,
    BButton,
    BAlert,
    BLink,
    Logo,
    SidebarSendEmail,
  },
  data() {
    return {
      printWithSignature: false,
      orderData: {},
    };
  },
  async created() {
    await this.getCommandData();
  },
  methods: {
    // external Filter
    externalDateFormat(date) {
      return DateFormat(date);
    },
    // external utils
    externalUserCurrency() {
      return GetLinkCurrency();
    },
    // Other function
    // print
    printInvoice(arg) {
      this.printWithSignature = arg;
      window.print();
    },
    saveOrderPurchase() {
      if (this.orderData.createdBy) {
        this.orderData.createdBy = this.orderData.createdBy._id;
      }
      if (this.orderData.updatedBy) {
        this.orderData.updatedBy = this.orderData.updatedBy._id;
      }
      if (this.orderData.supplier) {
        this.orderData.supplier = this.orderData.supplier._id;
      }
      this.orderData.products.forEach((element) => {
        element.product = element.product._id;
      });
      axios
        .post(
          "https://management-projects.herokuapp.com/api/purchase-orders",
          this.orderData
        )
        .then((response) => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Bon de commande bien ajoutée",
              icon: "EditIcon",
              variant: "success",
            },
          });
        })
        .catch((error) => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Erreur lors de l'ajout",
              icon: "EditIcon",
              variant: "danger",
            },
          });
        });
    },
    getCommandData() {
      if (this.$router.history.current.query.command) {
        axios
          .get(
            "https://management-projects.herokuapp.com/api/purchase-commands/" +
              this.$router.history.current.query.command
          )
          .then((response) => {
            this.orderData = response.data;
            console.log("eee", response.data);
          })
          .catch((error) => {
            this.errorMessage = error.message;
          });
      } else if (this.$router.history.current.query.order) {
        axios
          .get(
            "https://management-projects.herokuapp.com/api/purchase-orders/" +
              this.$router.history.current.query.order
          )
          .then((response) => {
            this.orderData = response.data;
            console.log("eee", response.data);
          })
          .catch((error) => {
            this.errorMessage = error.message;
          });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@core/scss/base/pages/app-invoice.scss";
</style>

<style lang="scss">
@media print {
  // Global Styles
  body {
    background-color: transparent !important;
  }
  nav.header-navbar {
    display: none;
  }
  .main-menu {
    display: none;
  }
  .header-navbar-shadow {
    display: none !important;
  }
  .content.app-content {
    margin-left: 0;
    padding-top: 2rem !important;
  }
  footer.footer {
    display: none;
  }
  .card {
    background-color: transparent;
    box-shadow: none;
  }
  .customizer-toggle {
    display: none !important;
  }

  // Invoice Specific Styles
  .invoice-preview-wrapper {
    .row.invoice-preview {
      .col-md-8 {
        max-width: 100%;
        flex-grow: 1;
      }

      .invoice-preview-card {
        .card-body:nth-of-type(2) {
          .row {
            > .col-12 {
              max-width: 50% !important;
            }

            .col-12:nth-child(2) {
              display: flex;
              align-items: flex-start;
              justify-content: flex-end;
              margin-top: 0 !important;
            }
          }
        }
      }
    }

    // Action Right Col
    .invoice-actions {
      display: none;
    }
  }
}
.product-description {
  font-size: 12px !important;
}
</style>
